/**
 * useDialogFocus.ts
 *
 * Shared modality wiring for SheetDialog and SideDrawer: Escape closes,
 * opening moves focus onto the panel, closing restores it to whatever had
 * focus beforehand. No focus trap (Tab can still leave the panel) — that's
 * a follow-up, not something either dialog needs today.
 *
 * Internal to packages/shell — not exported from index.ts. Both dialogs put
 * the returned ref on their panel element with `tabIndex={-1}` so it's
 * programmatically focusable without joining the Tab order.
 */

import { useEffect, useRef } from 'react';

export function useDialogFocus(open: boolean, onClose: () => void) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Every caller passes an inline (unstable) `onClose`, so it can't sit in
  // the effect's own deps without re-running this effect on every re-render
  // of an open dialog — which re-captured `document.activeElement` (by then
  // whatever the user had just focused inside the panel, e.g. AskSheet's
  // composer) and immediately refocused the panel div over it, so a caller
  // typing into a field re-rendering the open sheet fought this effect for
  // focus, killing typing. Keep the latest callback in a ref, read from
  // there in the Escape handler, and depend on `open` alone.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused.current?.focus();
    };
  }, [open]);

  return panelRef;
}
